// External
import React, { Component } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { sortBy } from "lodash";

// Internal
import NavBaseTemplate from "../templates/NavBaseTemplate";
import FeedbackTile from "../theme/modals/FeedbackTile";
import RowCard from "../cards/rowCard";
import DataTable from "../theme/tables/DataTable";
import BaseModal from "../theme/modals/BaseModal";
import BaseForm from "../theme/forms/BaseForm";
import { doorCanvassEditCreateForm } from "../../utils/forms/formFields";
import {
  pullCampaignOffices,
  pullCampaignScripts,
  updateOrCreateDoorCanvass,
} from "../../store/api/calls";
import {
  postDoorDataAction,
  pullCampaignDoorCanvasses,
  pullCampaignDoorCanvassPasses,
} from "../../store/api/calls/doors";
import PassModal from "../theme/modals/PassModal";

class DoorCanvassScreen extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      selectedOffice: 0,
      canvasses: [],
      allExpanded: false,
      passModalVisible: false,
      selectedCanvass: null,
      editModalOpen: false,
      editingInstance: {},
    };

    this.formInputs = doorCanvassEditCreateForm;
    this.DISPLAY_FORMAT = "M/DD/YY";
    this.dateChangeTimeout = null;
  }

  pullData(force = false) {
    this.setState({ loading: true });
    let requests = [];

    pullCampaignScripts();

    if (force || !this.props.offices.length) {
      requests.push(pullCampaignOffices());
    }

    if (force || !this.props.doorCanvasses.length) {
      requests.push(pullCampaignDoorCanvasses());
    }

    if (force || !this.props.doorCanvassPasses.length) {
      requests.push(pullCampaignDoorCanvassPasses());
    }

    // get data from any previous requests that may not be present in props first
    if (requests.length) {
      Promise.all(requests)
        .then((data) => {
          this.populateCreateEditForm();
          this.buildData();
        })
        .catch((err) => this.setState({ loading: false }));
    } else {
      this.populateCreateEditForm();
      this.buildData();
    }
  }

  populateCreateEditForm() {
    const offices = [{ value: null, display: "" }];
    this.props.offices.forEach((d) => {
      offices.push({ value: d.id, display: d.name });
    });

    const scripts = [{ value: null, display: "" }];
    sortBy(this.props.scripts, (d) => d.name?.toLowerCase()).forEach((d) => {
      scripts.push({ value: d.id, display: d.name });
    });

    this.formInputs = [
      {
        type: "text",
        title: "Name",
        dataKey: "name",
        placeholder: "Use something descriptive",
      },
      {
        type: "select",
        title: "Office",
        dataKey: "office",
        options: offices,
      },
      {
        type: "select",
        title: "Script",
        dataKey: "script",
        options: scripts,
      },
    ];
  }

  componentDidMount() {
    this.pullData(true);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.focusedCampaign !== this.props.focusedCampaign) {
      this.pullData(true);
    } else if (
      prevProps.doorCanvasses !== this.props.doorCanvasses ||
      prevProps.doorCanvassPasses !== this.props.doorCanvassPasses
    ) {
      this.buildData();
    }
  }

  buildData() {
    let canvasses = {};

    // buld canvass object structure, associate and passes and then put in state.
    // Offices / passes can land out of order vs doorCanvasses; guard nested reads.
    this.props.doorCanvasses.forEach((item, index) => {
      if (!canvasses[item.id]) {
        canvasses[item.id] = {
          id: item.id,
          title: item.name,
          name: item.name,
          office: item.office,
          script: item.script,
          passes: [],
          statuses: item.statuses,
        };
      }

      this.props.doorCanvassPasses.forEach((dcp) => {
        if (dcp.canvass?.id === item.id) {
          dcp.agg_data = dcp.agg_data || {};
          canvasses[item.id].passes.push(dcp);
        }
      });
    });

    // they are sorted by keys to make it easier to group. Remove from dictionary, and you have an array of canvasses
    canvasses = Object.values(canvasses);

    this.setState({
      loading: false,
      // Offices often arrive after canvasses/passes; componentDidUpdate can call
      // buildData before pullCampaignOffices resolves (especially on fast nav).
      selectedOffice: this.props.offices[0]?.id ?? this.state.selectedOffice,
      canvasses,
    });
  }

  filterCards(data) {
    if (data.office === this.state.selectedOffice) {
      return data.data;
    } else {
      return [];
    }
  }

  handleActionSelect = (val) => {
    console.log("selected", val);
  };

  onOpenEditModal = (modelInstance = {}) => {
    this.buildInputsWithPrepopulatedFields(modelInstance);
    this.setState({ editModalOpen: true, editingInstance: modelInstance });
  };

  onCloseEditModal = () => {
    this.setState({ editModalOpen: false, editingInstance: {} });
  };

  buildInputsWithPrepopulatedFields = (modelInstance) => {
    this.formInputs.forEach((d) => {
      d.value = modelInstance[d.dataKey];
    });
  };

  handleUpdateSubmission = (data) => {
    const id = this.state.editingInstance.id;
    const campaign = this.props.focusedCampaign.id;

    updateOrCreateDoorCanvass({ ...data, id, campaign }).then(
      this.onCloseEditModal
    );
  };

  getScriptName = (canvass) => {
    const scriptId =
      canvass.script ||
      canvass.passes?.[0]?.canvass?.script;
    return this.props.scripts.find((script) => script.id === scriptId)?.name;
  };

  buildPassTable = (canvass) => {
    let { passes } = canvass;
    if (!passes) passes = [];

    let get_percent_of_total = (numerator, denominator) => {
      if (!numerator) return "0%";
      if (!denominator) return "--";
      return Math.round((numerator / denominator) * 100) + "%";
    };

    let tableData = passes.map((pass) => {
      console.log(pass);
      let getDisp = (metric, style) => {
        if (!metric) return "--";
        return (
          <span className={"flx is-flex-direction-column"} style={style}>
            <span className={"is-size-4"}>{metric}</span>
            <span className={"is-size-6"}>
              ({get_percent_of_total(metric, pass.household_count)})
            </span>
          </span>
        );
      };

      let { agg_data } = pass;
      let walkable_cut = agg_data.walkable_cut || 0;
      let unwalkable_cut = agg_data.unwalkable_cut || 0;
      let total_cut = walkable_cut + unwalkable_cut;
      let knocked = agg_data.attempted || 0;

      return {
        "✂": pass.is_locked ? (
          <div className="flx">
            <span className={"danger-text"}>LOCKED</span>
          </div>
        ) : (
          <Link to={`/turfcutter/${pass.id}`}>
            <div className={"button button-clear m-0 p-0"}>
              <div className={"flx"}>
                <i
                  className="fa fa-cut fa-pull-right mr-2"
                  title="Go To Turf Cutter"
                />
                <span>Cut Turf</span>
              </div>
            </div>
          </Link>
        ),
        name: <span className={"has-text-semibold"}>{pass.name}</span>,

        total_doors: getDisp(pass.household_count),
        knocked: getDisp(knocked),
        cut_doors: getDisp(total_cut),
        walkable_cut: getDisp(walkable_cut),
        unwalkable_cut: getDisp(unwalkable_cut),
        walkable_turfs: getDisp(agg_data.walkable_turfs),
        unwalkable_turfs: getDisp(agg_data.unwalkable_turfs),
      };
    });

    return (
      <DataTable
        title={`Script:  ${this.getScriptName(canvass) || ""}`}
        data={tableData}
      />
    );
  };

  onCreatePass = (create_info) => {
    // "msg": {
    //   "type": "NEW_PASS",
    //       "parent_pass_id": 450,
    //       "filter_info": "--",
    //       "new_pass_name": "Autogenerated new pass from lambda tests",
    //       "statuses_to_remove": "refused and canvassed"

    // new_pass_name: "First Prod DB Canvass Pass 2"
    // parent_pass_id: 1
    // statuses: (5) ['canvass', 'moved', 'refused', 'language_barrier', 'do_not_walk']
    //     [[Prototype]]: Object
    // }

    /*
    create_info shape:

    {
      parent_pass_id: fromPass,
      new_pass_name: passName,
      statuses: statusArr,
    }

    */

    const data_to_dispatch = {
      ...create_info,
      statuses_to_remove: create_info.statuses,
      filter_info: "Removed " + create_info.statuses.join(", ") + ".",
      type: "NEW_PASS",
    };

    postDoorDataAction(data_to_dispatch).then(() => null);
  };

  renderCards() {
    let result = [];

    sortBy(this.state.canvasses, ["title", "id"]).forEach((canvass, index) => {
      if (!canvass.passes || !canvass.passes.length) return;

      result.push(
        <div className={"doorcanvass-set mb-6 pb-4"}>
          <h4 className="mb-2">{canvass.title}</h4>
          <div className="flx justify-start is-align-items-center mb-2">
            <Link to={`/doorcanvass/${canvass.id}/schedule`}>
              <button
                  className="button primary-milligram m-1"
                  data-testid={"doors-schedule-" + index}
              >
                <i className="fas fa-clock" style={{marginRight: '1em'}}/>
                Schedule
              </button>
            </Link>

            {this.props.is_superuser && (
              <button
                type="button"
                onClick={() => this.onOpenEditModal(canvass)}
                className="button primary-milligram m-1"
              >
                <i className="fas fa-edit" style={{ marginRight: "1em" }} />
                Edit
              </button>
            )}

            {this.props.is_admin && (
                <button
                    onClick={() => {
                      this.setState({
                        passModalVisible: true,
                        selectedCanvass: canvass,
                      });
                    }}
                    className={
                      "button primary-milligram m-1"
                    }
                >
                  <i className="fas fa-plus" style={{marginRight: '1em'}}/>
                  New Pass
                </button>
            )}
          </div>

          {this.buildPassTable(canvass)}
        </div>
      );
    });

    if (!result.length && !this.state.loading) {
      return (
        <RowCard
          columns={[{ text: "This office does not have any door canvasses" }]}
        />
      );
    }

    return result;
  }

  render() {
    return (
      <NavBaseTemplate>
        <div className={"button-table-top-wrapper"}>
          {/*<a href={'/doors-upload'} className={'button'}>*/}
          {/*  + Upload Doors Data*/}
          {/*</a>*/}
        </div>
        {this.renderCards()}
        <FeedbackTile
          text={
            <div>
              <i
                className="fas fa-asterisk fa-spin"
                style={{ marginRight: "15px" }}
              />
              Loading...
            </div>
          }
          visible={this.state.loading || this.props.doorCanvassPassLoading}
        />

        {/*use feedback tile icon, text, state*/}
        <FeedbackTile
          text={
            <div>
              <i
                className="fas fa-exclamation-triangle"
                style={{ marginRight: "15px" }}
              />
              There was a server error.
            </div>
          }
          visible={false}
          backgroundColor={"red"}
        />
        {this.state.passModalVisible && (
          <PassModal
            handlePassModalClose={() =>
              this.setState({ passModalVisible: false })
            }
            onSubmit={(create_info) => this.onCreatePass(create_info)}
            canvass={this.state.selectedCanvass}
          />
        )}

        {this.props.is_superuser && (
          <BaseModal
            open={this.state.editModalOpen}
            onClose={this.onCloseEditModal}
            center
          >
            <BaseForm
              inputs={this.formInputs}
              onCancel={this.onCloseEditModal}
              onSubmit={this.handleUpdateSubmission}
            />
          </BaseModal>
        )}
      </NavBaseTemplate>
    );
  }
}

const mapStateToProps = (state) => ({
  campaigns: state.campaigns?.data,
  focusedCampaign: state.session.focusedCampaign,
  is_admin: state.permissions.is_admin,
  is_superuser: state.permissions.is_superuser,
  offices: state.offices.data,
  doorCanvasses: state.doorCanvasses.data,
  doorCanvassPasses: state.doorCanvassPasses.data,
  doorCanvassPassLoading: state.doorCanvassPasses.loading,
  scripts: state.scripts.data,
});

export default connect(mapStateToProps)(DoorCanvassScreen);
